import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserFromLocalStorage } from '../../utilities/user';
import { apiFetchPartnerIncentives } from '../../api/index.js';
import {
    colors,
    font,
    text,
    card,
    cardBody,
    grid,
    button,
    pill,
    ugx,
} from './partnerTheme';
import PartnerHelp from './partnerHelp';

/**
 * Tier is the one label on this screen the partner is asked to care about, so
 * it gets a tone of its own rather than a generic chip. The tones are the
 * shared state tones, reused for rank: muted is the entry rank, good is the
 * top one, so climbing is visible at a glance and not just readable.
 */
const tierTone = (tier) => {
    if (tier === 'platinum') return 'good';
    if (tier === 'gold') return 'warn';
    if (tier === 'silver') return 'info';
    return 'muted';
};

const titleCase = (value) =>
    typeof value === 'string' && value.length
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : '';

const PartnerIncentives = () => {
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();
    const partnerId = user?.result?.partnerId || user?.result?._id;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [incentives, setIncentives] = useState(null);

    const load = useCallback(async () => {
        if (!partnerId) {
            setLoading(false);
            setError('No partner account is linked to this login, so incentives cannot be loaded.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const { data } = await apiFetchPartnerIncentives(partnerId);
            setIncentives(data?.data || null);
        } catch (err) {
            setError(
                err?.response?.data?.error?.message ||
                'Could not load incentives. Please try again.'
            );
            setIncentives(null);
        } finally {
            setLoading(false);
        }
    }, [partnerId]);

    useEffect(() => {
        load();
    }, [load]);

    const currentTier = incentives?.currentTier;
    const nextTier = incentives?.nextTier;
    const progress = incentives?.progress;
    const benefits = incentives?.nextTierBenefits || [];
    const bonuses = incentives?.bonuses;

    const completed = progress?.ordersCompleted;
    const target = progress?.target;
    // The server already clamps this to 100, so it is safe to use as a width.
    const percentage = Math.max(0, Math.min(100, Number(progress?.percentage) || 0));
    const remaining =
        nextTier && typeof completed === 'number' && typeof target === 'number'
            ? Math.max(0, target - completed)
            : null;

    return (
        <div className="content" style={{ fontFamily: font.sans, background: colors.ground }}>
            <div style={{ marginBottom: 20 }}>
                <h4 style={text.title}>Incentives</h4>
                <p style={text.subtitle}>
                    Your partnership tier, the deliveries that move you up, and the bonuses your
                    riders have already earned you.
                </p>
            </div>

            {error && (
                <div style={{ ...card, border: `1px solid ${colors.red}`, background: colors.redSoft }}>
                    <div style={{ ...cardBody, ...text.body, color: colors.red }}>{error}</div>
                </div>
            )}

            {loading && (
                <div style={card}>
                    <div style={{ ...cardBody, ...text.body }}>Loading incentives...</div>
                </div>
            )}

            {!loading && !error && incentives && (
                <>
                    <div style={card}>
                        <div style={cardBody}>
                            <div style={text.section}>
                                Your tier
                                <PartnerHelp topic="partnershipTier" />
                            </div>

                            {currentTier ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <span style={{ ...pill(tierTone(currentTier)), fontSize: 13, padding: '6px 16px' }}>
                                        {currentTier}
                                    </span>
                                    <span style={{ ...text.body, color: colors.muted }}>
                                        {nextTier
                                            ? `Next rank up is ${titleCase(nextTier)}.`
                                            : 'This is the highest partnership tier.'}
                                    </span>
                                </div>
                            ) : (
                                // A missing tier is not bronze. Guessing would tell the partner
                                // they have been ranked when nobody has ranked them.
                                <p style={{ ...text.body, color: colors.muted, margin: 0 }}>
                                    No partnership tier has been set on your account yet. Lusuku
                                    assigns one when your partnership terms are confirmed.
                                </p>
                            )}

                            <div style={{ ...grid(2, 20), marginTop: 24 }}>
                                <div>
                                    <div style={{ ...text.label, marginBottom: 6 }}>
                                        Bonuses earned at this tier
                                    </div>
                                    {bonuses && typeof bonuses.earned === 'number' ? (
                                        <div style={{ ...text.numeric, fontSize: 22, fontWeight: 700, color: colors.green }}>
                                            {ugx(bonuses.earned)}
                                        </div>
                                    ) : (
                                        <div style={{ ...text.body, color: colors.muted }}>
                                            Not reported yet.
                                        </div>
                                    )}
                                    <p style={text.help}>
                                        Total service level bonuses paid across your riders.
                                    </p>
                                </div>
                                <div>
                                    <div style={{ ...text.label, marginBottom: 6 }}>
                                        Bonuses available to claim
                                    </div>
                                    {/* The server does not track unclaimed bonuses yet, so it
                                        always sends zero here. Printing UGX 0 would read as a
                                        measured balance rather than a feature that is absent. */}
                                    {bonuses && bonuses.available > 0 ? (
                                        <div style={{ ...text.numeric, fontSize: 22, fontWeight: 700, color: colors.green }}>
                                            {ugx(bonuses.available)}
                                        </div>
                                    ) : (
                                        <div style={{ ...text.body, color: colors.muted }}>
                                            Nothing waiting to be claimed.
                                        </div>
                                    )}
                                    <p style={text.help}>
                                        Bonuses are settled with your regular payout run.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={card}>
                        <div style={cardBody}>
                            <div style={text.section}>
                                {nextTier ? `Progress to ${titleCase(nextTier)}` : 'Progress'}
                            </div>

                            {typeof completed === 'number' ? (
                                <>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'baseline',
                                            marginBottom: 10,
                                            gap: 12,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <span style={{ ...text.numeric, fontSize: 18, fontWeight: 700 }}>
                                            {completed.toLocaleString('en-UG')}
                                            {nextTier && typeof target === 'number'
                                                ? ` of ${target.toLocaleString('en-UG')} deliveries`
                                                : ' deliveries completed'}
                                        </span>
                                        <span style={{ ...text.body, color: colors.muted }}>
                                            {Math.round(percentage)}%
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            height: 10,
                                            borderRadius: 999,
                                            background: colors.lineSoft,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${percentage}%`,
                                                height: '100%',
                                                background: colors.green,
                                                borderRadius: 999,
                                                transition: 'width 0.3s',
                                            }}
                                        />
                                    </div>

                                    <p style={{ ...text.body, color: colors.muted, marginTop: 12, marginBottom: 0 }}>
                                        {nextTier && remaining !== null
                                            ? remaining > 0
                                                ? `${remaining.toLocaleString('en-UG')} more completed deliveries and you move to ${titleCase(nextTier)}.`
                                                : `You have met the delivery target for ${titleCase(nextTier)}. Lusuku promotes tiers on review.`
                                            : 'You are at the top tier, so there is no further target to hit.'}
                                    </p>
                                </>
                            ) : (
                                <p style={{ ...text.body, color: colors.muted, margin: 0 }}>
                                    No completed deliveries have been recorded against your
                                    account yet, so there is no progress to show.
                                </p>
                            )}
                        </div>
                    </div>

                    {benefits.length > 0 && (
                        <div style={card}>
                            <div style={cardBody}>
                                <div style={text.section}>
                                    {nextTier ? `What ${titleCase(nextTier)} gives you` : 'Your tier benefits'}
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {benefits.map((benefit) => (
                                        <li
                                            key={benefit}
                                            style={{ ...text.body, marginBottom: 8, lineHeight: 1.5 }}
                                        >
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </>
            )}

            {!loading && error && (
                <button type="button" style={button('secondary')} onClick={load}>
                    Try again
                </button>
            )}
        </div>
    );
};

export default PartnerIncentives;
